import { motion } from "framer-motion";
import { Check, ShieldCheck } from "lucide-react";
import { EQUIPMENT } from "@/constants/aero";

const ease = [0.16, 1, 0.3, 1];

const Highlights = ({ items, large = false }) => (
  <ul className={`mt-5 ${large ? "space-y-3" : "space-y-2.5"}`}>
    {items.map((h) => (
      <li key={h} className={`flex items-center gap-2.5 ${large ? "text-[15px]" : "text-sm"} text-[#6B7280]`}>
        <Check size={13} strokeWidth={2} className="text-[#4F6D8A] shrink-0" />
        {h}
      </li>
    ))}
  </ul>
);

export const EquipmentShowcase = () => {
  const hero = EQUIPMENT.find((e) => e.hero);
  const rest = EQUIPMENT.filter((e) => !e.hero);

  return (
    <div className="mt-24" data-testid="equipment-showcase">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, ease }}
        className="max-w-2xl"
      >
        <p className="font-mono text-[12px] tracking-[0.35em] uppercase text-[#4F6D8A]">Production Gear</p>
        <h3 className="mt-4 font-serif text-[34px] md:text-[48px] xl:text-[56px] leading-[1.05] text-[#111827]">Equipment We Use</h3>
        <motion.span
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.25, ease }}
          className="mt-5 block w-14 h-[2px] bg-[#4F6D8A] origin-left"
        />
        <p className="mt-5 text-[#6B7280] leading-relaxed">
          Professional cinema-grade equipment trusted for feature films, OTT productions, government documentaries, and commercial shoots.
        </p>
      </motion.div>

      <div className="mt-14 grid md:grid-cols-2 gap-7">
        {/* Hero card — DJI Inspire 3 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-30px" }}
          transition={{ duration: 0.7, ease }}
          data-testid="equipment-card-hero"
          className="group md:col-span-2 bg-white rounded-[22px] border border-[#E5E7EB] overflow-hidden shadow-[0_15px_50px_-15px_rgba(15,42,67,0.2)] hover:-translate-y-1.5 hover:shadow-[0_40px_80px_-25px_rgba(15,42,67,0.4)] transition-all duration-300 grid sm:grid-cols-2"
        >
          <div className="relative h-64 sm:h-auto overflow-hidden bg-[#F8F6F2]">
            <img
              src={hero.img}
              alt={hero.name}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <span className="absolute top-5 left-5 inline-flex items-center gap-2 bg-[#0F2A43] text-white font-mono text-[8px] font-bold tracking-[0.25em] uppercase px-4 py-2 rounded-full">
              Cinema Flagship
            </span>
          </div>
          <div className="p-8 md:p-12 flex flex-col justify-center">
            <p className="font-display font-bold text-2xl md:text-3xl text-[#111827] leading-tight">{hero.name}</p>
            <p className="mt-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#4F6D8A]">{hero.sub}</p>
            <Highlights items={hero.highlights} large />
          </div>
        </motion.div>

        {/* Remaining cards */}
        {rest.map((e, i) => (
          <motion.div
            key={e.name}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.55, delay: (i % 2) * 0.08, ease }}
            data-testid={`equipment-card-${i}`}
            className="group bg-white rounded-[22px] border border-[#E5E7EB] overflow-hidden shadow-[0_10px_35px_-15px_rgba(15,42,67,0.15)] hover:-translate-y-1.5 hover:shadow-[0_35px_70px_-20px_rgba(15,42,67,0.4)] transition-all duration-300"
          >
            <div className="h-52 overflow-hidden bg-[#F8F6F2]">
              <img
                src={e.img}
                alt={e.name}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="p-7">
              <p className="font-display font-bold text-xl text-[#111827]">{e.name}</p>
              <p className="mt-1.5 font-mono text-[9px] tracking-[0.22em] uppercase text-[#4F6D8A]">{e.sub}</p>
              <Highlights items={e.highlights} />
            </div>
          </motion.div>
        ))}
      </div>

      {/* Trust banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.6, ease }}
        data-testid="equipment-trust-banner"
        className="mt-12 bg-[#F8F6F2] border border-[#E5E7EB] rounded-full px-8 py-6 flex items-center justify-center gap-4 text-center"
      >
        <ShieldCheck size={18} strokeWidth={1.5} className="text-[#4F6D8A] shrink-0 hidden sm:block" />
        <p className="text-sm md:text-base text-[#374151]">
          Professional cinema-grade equipment trusted across <span className="text-[#0F2A43] font-medium">feature films, OTT series, and government productions.</span>
        </p>
      </motion.div>
    </div>
  );
};
